import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import StatCard from '../components/Dashboard/StatCard';
import PendingActionItem from '../components/Dashboard/PendingActionItem';
import QuickAction from '../components/Dashboard/QuickAction';
import DashboardService, { DashboardStats } from '../services/DashboardService';

const HomePage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardStats = async () => {
      try {
        const data = await DashboardService.getDashboardStats();
        setStats(data);
      } catch (error) {
        console.error('Failed to load dashboard stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardStats();
  }, []);
  return (
    <div className="space-y-8">
      {/* Stats Grid */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        {loading ? (
          <>
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="rounded-xl border border-[#F2F4F7] bg-white p-6 shadow-[0_4px_12px_0_rgba(249,250,251,1)]">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="h-4 w-24 bg-gray-200 rounded animate-pulse mb-3"></div>
                    <div className="h-8 w-16 bg-gray-200 rounded animate-pulse mb-2"></div>
                    <div className="h-3 w-20 bg-gray-200 rounded animate-pulse"></div>
                  </div>
                  <div className="h-12 w-12 bg-gray-200 rounded-full animate-pulse"></div>
                </div>
              </div>
            ))}
          </>
        ) : (
          <>
            <StatCard
              title="Total Coaches"
              value={stats?.total_coaches || 0}
              subtitle="See all coaches"
              iconBgColor="bg-blue-100"
              iconColor="text-blue-600"
              onClick={() => navigate('/coaches')}
              icon={
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
              }
            />
            <StatCard
              title="Total Students"
              value={stats?.total_students || 0}
              subtitle="See all Students"
              iconBgColor="bg-green-100"
              iconColor="text-green-600"
              onClick={() => navigate('/students')}
              icon={
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                  />
                </svg>
              }
            />
            <StatCard
              title="Active Engagements"
              value={stats?.active_engagements || 0}
              subtitle="Coach-student pairs"
              iconBgColor="bg-orange-100"
              iconColor="text-orange-600"
              onClick={() => navigate('/requests')}
              icon={
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                  />
                </svg>
              }
            />
            <StatCard
              title="Upcoming Sessions"
              value={stats?.upcoming_sessions || 0}
              subtitle="This week"
              iconBgColor="bg-purple-100"
              iconColor="text-purple-600"
              onClick={() => navigate('/sessions')}
              icon={
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
            </svg>
          }
        />
          </>
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Pending Actions */}
        <div className="lg:col-span-2">
          <div className="rounded-xl border border-[#F2F4F7] bg-white p-6 shadow-[0_4px_12px_0_rgba(249,250,251,1)]">
            <div className="mb-6 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Pending Actions</h2>
              <span className="text-sm font-medium text-gray-400">
                {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </span>
            </div>
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="rounded-xl border border-[#F2F4F7] bg-gray-50 p-5">
                    <div className="flex items-center gap-4">
                      <div className="h-10 w-10 bg-gray-200 rounded-full animate-pulse"></div>
                      <div className="flex-1">
                        <div className="h-4 w-3/4 bg-gray-200 rounded animate-pulse mb-2"></div>
                        <div className="h-3 w-1/2 bg-gray-200 rounded animate-pulse"></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-4">
              {stats?.pending_requests ? (
                <div onClick={() => navigate('/requests')} className="cursor-pointer">
                  <PendingActionItem
                    icon={
                      <svg
                        className="h-5 w-5 text-orange-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                          clipRule="evenodd"
                        />
                      </svg>
                    }
                    iconBgColor="bg-orange-100"
                    title={`${stats.pending_requests} Student Requests Awaiting Assignment`}
                    description="Review and assign coaches to new students"
                  />
                </div>
              ) : null}
              {stats?.open_reports ? (
                <PendingActionItem
                  icon={
                    <svg
                      className="h-5 w-5 text-red-600"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                  }
                  iconBgColor="bg-red-100"
                  title={`${stats.open_reports} Open Student Reports`}
                  description="Review and resolve student concerns"
                />
              ) : null}
              {!stats?.pending_requests && !stats?.open_reports && (
                <div className="text-center py-8">
                  <p className="text-gray-500 text-sm">No pending actions</p>
                </div>
              )}
            </div>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="lg:col-span-1">
          <div className="rounded-xl border border-[#F2F4F7] bg-white p-6 shadow-[0_4px_12px_0_rgba(249,250,251,1)]">
            <h2 className="mb-6 text-xl font-semibold text-gray-900">Quick Actions</h2>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="rounded-xl border border-[#F2F4F7] bg-gray-50 p-4">
                    <div className="flex items-start gap-3">
                      <div className="h-10 w-10 bg-gray-200 rounded-full animate-pulse flex-shrink-0"></div>
                      <div className="flex-1">
                        <div className="h-4 w-3/4 bg-gray-200 rounded animate-pulse mb-2"></div>
                        <div className="h-3 w-full bg-gray-200 rounded animate-pulse"></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-3">
              <div onClick={() => navigate('/coaches')} className="cursor-pointer">
                <QuickAction
                  icon={
                    <svg className="h-5 w-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                  }
                  title="Add a new Coach"
                  description="Onboard a new coach and send them portal access"
                />
              </div>
              <div onClick={() => navigate('/students')} className="cursor-pointer">
                <QuickAction
                  icon={
                    <svg className="h-5 w-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                  }
                  title="Add a new Student"
                  description="Register a new student and assign them to a coach"
                />
              </div>
              <div onClick={() => navigate('/requests')} className="cursor-pointer">
                <QuickAction
                  icon={
                    <svg className="h-5 w-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                      />
                    </svg>
                  }
                  title="View Requests"
                  description={`${stats?.pending_requests || 0} pending ${stats?.pending_requests === 1 ? 'assignment' : 'assignments'}`}
                />
              </div>
              <QuickAction
                icon={
                  <svg className="h-5 w-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                }
                title="View Sessions"
                description={`${stats?.upcoming_sessions || 0} ${stats?.upcoming_sessions === 1 ? 'session' : 'sessions'} scheduled this week`}
              />
            </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
